import i18next from 'i18next'
import { retrieveErrorFromApi } from '../../../helper'
import { setAppErrors } from '../../action/system'
import { appendInvoices, updateInvoiceCanceled, setInvoices, setInvoicesTotalLength, setSelectedInvoice, setSelectedInvoiceOrder, setUnpaidInvoicesCounter } from '../../action/invoice'
import fetchInvoices, { createPaidOrderInvoice } from '../../axios/invoice'
import getInvoice, { fetchOrderInvoices } from '../../axios/invoice/get'
import updateInvoiceStatus from '../../axios/invoice/status'
import updateInvoiceCustomer from '../../axios/invoice/customer'
import sendInvoice from '../../axios/invoice/send'
import correctInvoice from '../../axios/invoice/corrections'
import { selectInvoicesStatusFilter } from '../../selector/invoice'
import getOrder from '../../axios/orderview/get'
import setOrderStatus from '../../axios/order/setOrderStatus'

const NOT_INVOICEABLE_ORDER_STATUSES = [404, 409]

const findActiveInvoice = invoices => invoices.find(invoice => invoice.status !== 'CANCELED') ?? null

const findCurrentInvoice = invoices => findActiveInvoice(invoices) ?? invoices[0] ?? null

export const getInvoicesThunk = (page, size) => (dispatch, getState) => {
	return fetchInvoices(page, size, selectInvoicesStatusFilter(getState()))
		.then(res => {
			dispatch(setInvoicesTotalLength(res.data.page.totalElements))
			if (page > 0 && size > 0) {
				dispatch(appendInvoices(res.data.page.content))
				return
			}
			dispatch(setInvoices(res.data.page.content))
			dispatch(setUnpaidInvoicesCounter(res.data.totalUnpaid))
		}).catch(error => {
			dispatch(setAppErrors(retrieveErrorFromApi(error)))
		})
}

export const getInvoiceThunk = (invoiceUuid) => async (dispatch) => {
	try {
		const res = await getInvoice(invoiceUuid)
		dispatch(setSelectedInvoice(res.data))
	} catch (error) {
		dispatch(setAppErrors(retrieveErrorFromApi(error)))
	}
}

export const markInvoicePaidThunk = (invoiceUuid) => async (dispatch) => {
	try {
		await updateInvoiceStatus(invoiceUuid, true)
		return dispatch(getInvoicesThunk(0, 10))
	} catch (error) {
		dispatch(setAppErrors(retrieveErrorFromApi(error)))
	}
}

export const markInvoiceUnpaidThunk = (invoiceUuid) => async (dispatch) => {
	try {
		await updateInvoiceStatus(invoiceUuid, false)
		return dispatch(getInvoicesThunk(0, 10))
	} catch (error) {
		dispatch(setAppErrors(retrieveErrorFromApi(error)))
	}
}

export const cancelInvoiceOrderThunk = (invoiceId, order, reason, reasonCode) => async (dispatch) => {
	try {
		await setOrderStatus(order.orderId, { status: 'CANCELED', reason, reasonCode })
		dispatch(updateInvoiceCanceled(invoiceId, new Date().toISOString()))
		return true
	} catch (error) {
		dispatch(setAppErrors(retrieveErrorFromApi(error)))
		return false
	}
}

export const getOrderInvoicesThunk = (orderId) => async (dispatch) => {
	try {
		const res = await fetchOrderInvoices(orderId)
		return { activeInvoice: findActiveInvoice(res.data.page.content), currentInvoice: findCurrentInvoice(res.data.page.content), orderInvoiceable: res.data.orderInvoiceable }
	} catch {
		return null
	}
}

export const getInvoiceByOrderIdThunk = (orderId) => async (dispatch) => {
	const orderInvoices = await dispatch(getOrderInvoicesThunk(orderId))
	return orderInvoices?.activeInvoice ?? null
}

export const getCurrentOrderInvoiceThunk = (orderId) => async (dispatch) => {
	try {
		const res = await fetchOrderInvoices(orderId)
		return findCurrentInvoice(res.data.page.content)
	} catch (error) {
		dispatch(setAppErrors(retrieveErrorFromApi(error)))
		return null
	}
}

export const createPaidOrderInvoiceThunk = (orderId, recipient) => async (dispatch) => {
	try {
		const res = await createPaidOrderInvoice(orderId, recipient)
		return res.data
	} catch (error) {
		if (NOT_INVOICEABLE_ORDER_STATUSES.includes(error.response?.status)) {
			dispatch(setAppErrors([{ message: i18next.t('order.modal.create_invoice_not_possible') }]))
		} else {
			dispatch(setAppErrors(retrieveErrorFromApi(error)))
		}
		return null
	}
}

export const getInvoiceOrderThunk = (orderId) => async (dispatch) => {
	try {
		const res = await getOrder(orderId)
		dispatch(setSelectedInvoiceOrder(res.data))
	} catch (error) {
		dispatch(setAppErrors(retrieveErrorFromApi(error)))
	}
}

export const updateInvoiceCustomerThunk = (invoiceUuid, data) => async (dispatch) => {
	try {
		await updateInvoiceCustomer(invoiceUuid, data)
		return dispatch(getInvoiceThunk(invoiceUuid))
	} catch (error) {
		dispatch(setAppErrors(retrieveErrorFromApi(error)))
	}
}

export const sendInvoiceEmailThunk = (invoiceUuid, email) => async (dispatch) => {
	try {
		await sendInvoice(invoiceUuid, email)
		if (email) {
			await dispatch(getInvoicesThunk(0, 10))
		}
	} catch (error) {
		dispatch(setAppErrors(retrieveErrorFromApi(error)))
	}
}

export const correctInvoiceThunk = (invoiceUuid, recipient) => async (dispatch) => {
	try {
		const res = await correctInvoice(invoiceUuid, recipient)
		await dispatch(getInvoicesThunk(0, 10))
		return res.data
	} catch (error) {
		dispatch(setAppErrors(retrieveErrorFromApi(error)))
		return null
	}
}
